import * as Keychain from 'react-native-keychain';
import { generateSalt } from './DocumentEncryptionV2';
import { createLogger } from '../../utils/logger';

const logger = createLogger('UserSalt');

/**
 * Dedicated keychain service name for the per-user PBKDF2 salt. The salt is
 * generated once on first launch and persists for the lifetime of the install.
 */
export const USER_SALT_KEYCHAIN_SERVICE = 'myfinalkey.user_salt';

// Placeholder username — the salt is the actual secret; the username field is
// unused but required by Keychain's generic password API.
const KEYCHAIN_USERNAME = 'user_salt';

// Module-level cache so a single session doesn't re-prompt for biometry on
// every encrypt/decrypt call. Cleared on logout via `clearCachedSalt`.
let cachedSalt: string | null = null;

/**
 * Return the per-user salt, generating and persisting a fresh one on first
 * access. Never logs the value. Never returned to AsyncStorage or the network.
 */
export async function getOrCreateUserSalt(): Promise<string> {
  if (cachedSalt) return cachedSalt;

  const existing = await Keychain.getGenericPassword({
    service: USER_SALT_KEYCHAIN_SERVICE,
  });
  if (existing && typeof existing !== 'boolean' && existing.password) {
    cachedSalt = existing.password;
    return cachedSalt;
  }

  const fresh = await generateSalt();
  const stored = await Keychain.setGenericPassword(
    KEYCHAIN_USERNAME,
    fresh,
    {
      service: USER_SALT_KEYCHAIN_SERVICE,
      accessControl: Keychain.ACCESS_CONTROL.BIOMETRY_CURRENT_SET,
      accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
    },
  );
  if (!stored) {
    throw new Error('UserSalt: failed to persist salt to keychain');
  }

  logger.debug('User salt generated and persisted');
  cachedSalt = fresh;
  return fresh;
}

/**
 * Drop the in-memory salt cache. Called from the AppState 'background' /
 * 'inactive' handler in App.tsx and from logoutWipe on logout, so a
 * subsequent decrypt is forced to re-read the keychain (which re-prompts
 * biometry under BIOMETRY_CURRENT_SET).
 *
 * JS has no memset / secure-erase primitive — the best we can do is null
 * the reference so the GC can reclaim the underlying buffer. The actual
 * bytes may linger in a free heap page until something overwrites them;
 * this is a known limitation of managed-memory runtimes.
 */
export function clearCachedSalt(): void {
  cachedSalt = null;
}

/**
 * Permanently remove the stored salt. Call only on full account wipe — this
 * renders all v2 ciphertext derived from the old salt unrecoverable.
 */
export async function deleteUserSalt(): Promise<void> {
  cachedSalt = null;
  await Keychain.resetGenericPassword({
    service: USER_SALT_KEYCHAIN_SERVICE,
  });
}
